document.documentElement.classList.remove('no-js');

function setMobileMenuOpen(open) {
  const nav = document.querySelector('[data-mobile-nav]');
  const toggle = document.querySelector('[data-mobile-toggle]');
  if (!nav || !toggle) return;
  nav.hidden = !open;
  toggle.setAttribute('aria-expanded', String(open));
}

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-mobile-toggle]');
  if (toggle) {
    const nav = document.querySelector('[data-mobile-nav]');
    setMobileMenuOpen(Boolean(nav && nav.hidden));
    return;
  }
  const nav = document.querySelector('[data-mobile-nav]');
  if (nav && !nav.hidden && event.target.closest('[data-mobile-nav] a[href]')) {
    setMobileMenuOpen(false);
    return;
  }
  if (nav && !nav.hidden && !event.target.closest('[data-mobile-nav]') && !event.target.closest('[data-mobile-toggle]')) {
    setMobileMenuOpen(false);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') setMobileMenuOpen(false);
});

window.addEventListener('resize', () => {
  if (window.innerWidth > 1100) setMobileMenuOpen(false);
}, { passive: true });

function setCookie(name, value, days) {
  const maxAge = Math.max(1, days) * 86400;
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)};path=/;max-age=${maxAge};SameSite=Lax`;
}

function getCookie(name) {
  const target = `${encodeURIComponent(name)}=`;
  return document.cookie.split(';').map(v => v.trim()).find(v => v.startsWith(target))?.slice(target.length) || '';
}

class MaranteLocalizationForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-language-form]');
    this.input = this.querySelector('[data-language-input]');
    this.button = this.querySelector('[data-language-toggle]');
    this.menu = this.querySelector('[data-language-menu]');
    if (!this.form || !this.input || !this.button || !this.menu || this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.button.addEventListener('click', () => {
      const nextOpen = this.menu.hidden;
      document.querySelectorAll('[data-language-menu]').forEach(menu => { menu.hidden = true; });
      document.querySelectorAll('[data-language-toggle]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      this.menu.hidden = !nextOpen;
      this.button.setAttribute('aria-expanded', String(nextOpen));
    });

    this.querySelectorAll('[data-language-value]').forEach(option => {
      option.addEventListener('click', event => {
        event.preventDefault();
        const code = option.dataset.languageValue;
        if (!code) return;
        // A deliberate customer selection always wins over IP/browser auto-localization.
        setCookie('marante_locale', code, 365);
        this.input.value = code;
        this.form.submit();
      });
    });

    this.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        this.menu.hidden = true;
        this.button.setAttribute('aria-expanded', 'false');
        this.button.focus();
      }
    });
  }
}

if (!customElements.get('localization-form')) {
  customElements.define('localization-form', MaranteLocalizationForm);
}

document.addEventListener('click', event => {
  if (event.target.closest('[data-language-switcher]')) return;
  document.querySelectorAll('[data-language-menu]').forEach(menu => { menu.hidden = true; });
  document.querySelectorAll('[data-language-toggle]').forEach(button => button.setAttribute('aria-expanded', 'false'));
});

function submitLanguage(code, formEl) {
  const input = formEl?.querySelector('[data-language-input]');
  const form = formEl?.querySelector('[data-language-form]');
  const available = [...(formEl?.querySelectorAll('[data-language-value]') || [])].map(el => String(el.dataset.languageValue || '').toLowerCase());
  if (!input || !form || !available.includes(String(code || '').toLowerCase())) return false;
  input.value = code;
  form.submit();
  return true;
}

function preferredLocaleForCountry(countryCode) {
  const country = String(countryCode || '').toUpperCase();
  const browser = String(navigator.language || '').toLowerCase();
  const map = {
    HR: 'hr',
    DE: 'de', AT: 'de',
    IT: 'it',
    ES: 'es',
    NL: 'nl',
    PL: 'pl',
    CZ: 'cs',
    SK: 'sk',
    SI: 'sl'
  };
  if (country === 'BE') {
    if (browser.startsWith('nl')) return 'nl';
    if (browser.startsWith('de')) return 'de';
    return 'fr';
  }
  return map[country] || '';
}

function initLocalizationPreference() {
  const switcher = document.querySelector('[data-language-switcher]');
  const menu = switcher?.querySelector('[data-language-menu]');
  if (!switcher || !menu) return;

  const current = String(menu.dataset.currentLanguage || '').toLowerCase();
  const manual = decodeURIComponent(getCookie('marante_locale') || '').toLowerCase();

  // Manual choice survives VPNs/travel and overrides the detected market language.
  if (manual && manual !== current) {
    const guard = `manual:${manual}`;
    if (sessionStorage.getItem('marante_locale_redirect') !== guard) {
      sessionStorage.setItem('marante_locale_redirect', guard);
      if (submitLanguage(manual, switcher)) return;
    }
  }

  // On a first visit, use Shopify's resolved country (when automatic country/region
  // redirection is enabled) to pick the corresponding published language.
  if (!manual) {
    const auto = preferredLocaleForCountry(menu.dataset.currentCountry);
    if (auto && auto !== current) {
      const guard = `auto:${menu.dataset.currentCountry}:${auto}`;
      if (sessionStorage.getItem('marante_locale_redirect') !== guard) {
        sessionStorage.setItem('marante_locale_redirect', guard);
        submitLanguage(auto, switcher);
      }
    }
  }
}

window.addEventListener('DOMContentLoaded', initLocalizationPreference);

function formatMoney(cents, format) {
  const value = Number(cents || 0) / 100;
  const withComma = value.toLocaleString(document.documentElement.lang || 'en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const withDot = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return String(format || '€{{amount_with_comma_separator}}')
    .replace(/{{\s*amount_with_comma_separator\s*}}/g, withComma)
    .replace(/{{\s*amount\s*}}/g, withDot)
    .replace(/{{\s*amount_no_decimals\s*}}/g, Math.round(value).toLocaleString(document.documentElement.lang || 'en'))
    .replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/g, Math.round(value).toLocaleString(document.documentElement.lang || 'en'));
}

function initProduct(root) {
  const variantJson = root.querySelector('[data-product-variants]');
  const moneyJson = root.querySelector('[data-money-format]');
  const idInput = root.querySelector('[data-variant-id]');
  if (!variantJson || !idInput) return;

  let variants = [];
  let moneyFormat = '€{{amount_with_comma_separator}}';
  try { variants = JSON.parse(variantJson.textContent); } catch (_) {}
  try { moneyFormat = JSON.parse(moneyJson?.textContent || 'null') || moneyFormat; } catch (_) {}

  const groups = [...root.querySelectorAll('[data-option-position]')];
  const orderedGroups = () => [...groups].sort((a, b) => Number(a.dataset.optionPosition || 0) - Number(b.dataset.optionPosition || 0));
  const hadExplicitVariant = new URL(window.location.href).searchParams.has('variant');
  const price = root.querySelector('[data-product-price]');
  const compare = root.querySelector('[data-product-compare]');
  const identifier = root.querySelector('[data-product-identifier]');
  const add = root.querySelector('[data-add-to-cart]');
  const addLabel = root.querySelector('[data-add-label]');
  // The mobile buy bar is rendered just outside the product root section, so it
  // must be resolved from the document rather than root.querySelector().
  const stickyPrice = document.querySelector('[data-mobile-buybar] [data-sticky-price]');
  const stickyAdd = document.querySelector('[data-mobile-buybar] [data-sticky-add-label]');
  const stickyButton = document.querySelector('[data-mobile-buybar] button');
  const labels = {
    add: root.dataset.labelAdd || 'Add to cart',
    soldout: root.dataset.labelSoldout || 'Sold out',
    unavailable: root.dataset.labelUnavailable || 'Unavailable',
    fileEmpty: root.dataset.labelFileEmpty || 'No file selected',
    fileSelected: root.dataset.labelFileSelected || 'Selected',
    colorsDiffer: root.dataset.labelColorsDiffer || 'Choose two different colors.',
  };

  const carStyleGroup = root.querySelector('[data-car-style-option]');
  const carColorGroup = root.querySelector('[data-car-color-option]');
  const primaryColorGroup = root.querySelector('[data-primary-personalization-color]');
  const secondaryColorGroup = root.querySelector('[data-secondary-personalization-color]');
  const dimensionCard = root.querySelector('[data-dimension-card]');
  const dimensionChoices = dimensionCard ? [...dimensionCard.querySelectorAll('[data-dimension-choice]')] : [];

  function formatScaledDimensions(base, scale) {
    return String(base || '').split('×').map(function (part) {
      const value = parseFloat(part.trim());
      if (!Number.isFinite(value)) return part.trim();
      const scaled = value * scale;
      const rounded = Math.round((scaled + Number.EPSILON) * 100) / 100;
      return String(rounded).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
    }).join(' × ') + ' cm';
  }

  function syncDimensionCard() {
    if (!dimensionCard) return;
    const display = dimensionCard.querySelector('[data-size-dimensions]');
    const input = dimensionCard.querySelector('[data-size-dimensions-input]');
    const sizeInput = dimensionCard.querySelector('[data-size-dimensions-size]');
    const selected = root.querySelector('[data-option-value][data-dimension-scale]:checked');
    const selectedValueId = String(selected?.value || '');
    const selectedChoice = dimensionChoices.find(choice => String(choice.dataset.dimensionValueId || '') === selectedValueId);
    const base = display?.dataset.baseDimensions || '';
    const rawScale = selectedChoice?.dataset.dimensionScale || selected?.dataset.dimensionScale || '1';
    const scale = Number.parseFloat(rawScale);
    const dimensions = base ? formatScaledDimensions(base, Number.isFinite(scale) ? scale : 1) : '';

    if (display && dimensions) display.textContent = dimensions;
    if (input && dimensions) input.value = dimensions;
    if (sizeInput) sizeInput.value = selected?.dataset.dimensionSizeName || 'Standard';

    dimensionChoices.forEach(choice => {
      const active = Boolean(selected && String(selected.value) === String(choice.dataset.dimensionValueId));
      choice.classList.toggle('is-active', active);
      choice.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function chooseDimension(choice) {
    const valueId = String(choice?.dataset.dimensionValueId || '');
    if (!valueId) return;
    const target = [...root.querySelectorAll('[data-option-value][data-dimension-scale]')].find(input => String(input.value) === valueId);
    if (!target) return;
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  dimensionChoices.forEach(choice => choice.addEventListener('click', () => chooseDimension(choice)));

  function selectedOptionIds() {
    return orderedGroups().map(group => String(group.querySelector('[data-option-value]:checked')?.value || ''));
  }

  function carStyleMode() {
    return carStyleGroup?.querySelector('[data-option-value]:checked')?.dataset.carStyleValue || 'mono';
  }

  function applyPreferredVariantDefaults() {
    if (hadExplicitVariant) return;
    groups.forEach(group => {
      if (!group.dataset.defaultColor) return;
      const preferred = group.querySelector('[data-option-value][data-preferred-default="true"]');
      if (preferred && !preferred.disabled && !preferred.nextElementSibling?.classList.contains('is-unavailable')) preferred.checked = true;
    });
    if (carStyleGroup) {
      const mono = carStyleGroup.querySelector('[data-car-style-value="mono"]');
      if (mono && !mono.disabled) mono.checked = true;
    }
    const standardDimension = root.querySelector('[data-option-value][data-dimension-scale="1"]');
    if (standardDimension && !standardDimension.disabled) standardDimension.checked = true;
  }

  function updateFulfillmentMetadata() {
    const multi = carStyleMode() === 'multi';
    groups.forEach(group => {
      const checked = group.querySelector('[data-option-value]:checked');
      const hidden = group.querySelector('[data-fulfillment-color]');
      if (hidden && checked?.dataset.fulfillmentValue) hidden.value = multi && group === carColorGroup ? 'Multi-color' : checked.dataset.fulfillmentValue;
    });
  }

  function colorKey(input, property = false) {
    const raw = property ? input?.dataset.propertyColorKey : input?.dataset.colorKey;
    return String(raw || '').trim().toLowerCase();
  }

  function setColorChoiceConflict(input, conflict) {
    if (!input) return;
    input.disabled = Boolean(conflict);
    const label = input.nextElementSibling;
    if (label?.matches('label')) {
      label.classList.toggle('is-color-conflict', Boolean(conflict));
      if (conflict) label.setAttribute('aria-disabled', 'true');
      else label.removeAttribute('aria-disabled');
    }
  }

  function ensureDistinctPersonalizationColors({ repair = false } = {}) {
    if (!primaryColorGroup || !secondaryColorGroup || carStyleMode() === 'multi') {
      // Multi-colour artwork is intentionally allowed to include the key-rack colour.
      if (primaryColorGroup && secondaryColorGroup) {
        primaryColorGroup.querySelectorAll('[data-option-value][data-color-key]').forEach(input => setColorChoiceConflict(input, false));
        secondaryColorGroup.querySelectorAll('[data-property-color-input][data-property-color-key]').forEach(input => setColorChoiceConflict(input, false));
      }
      return true;
    }

    const primaryInputs = [...primaryColorGroup.querySelectorAll('[data-option-value][data-color-key]')];
    const secondaryInputs = [...secondaryColorGroup.querySelectorAll('[data-property-color-input][data-property-color-key]')];
    let primary = primaryColorGroup.querySelector('[data-option-value]:checked');
    let secondary = secondaryColorGroup.querySelector('[data-property-color-input]:checked');

    if (repair && primary && secondary && colorKey(primary) === colorKey(secondary, true)) {
      const replacement = secondaryInputs.find(input => colorKey(input, true) !== colorKey(primary));
      if (replacement) {
        replacement.checked = true;
        secondary = replacement;
        const current = secondaryColorGroup.querySelector('[data-property-color-current]');
        const canonical = secondaryColorGroup.querySelector('[data-property-color-canonical]');
        if (current) current.textContent = replacement.dataset.propertyLabel || replacement.value || '';
        if (canonical) canonical.value = replacement.dataset.propertyCanonical || replacement.value || '';
      }
    }

    const primaryKey = colorKey(primary);
    const secondaryKey = colorKey(secondary, true);
    primaryInputs.forEach(input => setColorChoiceConflict(input, Boolean(secondaryKey) && colorKey(input) === secondaryKey && !input.checked));
    secondaryInputs.forEach(input => setColorChoiceConflict(input, Boolean(primaryKey) && colorKey(input, true) === primaryKey && !input.checked));

    primary = primaryColorGroup.querySelector('[data-option-value]:checked');
    secondary = secondaryColorGroup.querySelector('[data-property-color-input]:checked');
    const valid = !primary || !secondary || colorKey(primary) !== colorKey(secondary, true);
    const note = secondaryColorGroup.querySelector('[data-color-constraint-note]');
    if (note) note.classList.toggle('is-error', !valid);
    return valid;
  }

  function syncCarStyleUI() {
    // Mono-color / Multi-color is the only customer-facing choice.
    // Marante handles the actual colour treatment internally; customers do not
    // select individual car-detail colours.
    ensureDistinctPersonalizationColors({ repair: true });
  }

  function update() {
    const ordered = orderedGroups();
    const selectedInputs = ordered.map(group => group.querySelector('[data-option-value]:checked'));
    const selected = selectedInputs.map(input => String(input?.value || ''));
    const selectedLabels = selectedInputs.map(input => String(input?.dataset.optionLabel || input?.value || ''));

    // Products with a single Shopify default variant have no option groups.
    // In that case there is nothing for the customer to select, so resolve the
    // product's selected/first available variant directly instead of treating
    // the missing option selection as "Unavailable".
    let variant = null;
    if (ordered.length === 0) {
      const currentId = String(idInput.value || '');
      variant = variants.find(v => String(v.id) === currentId) || variants.find(v => v.available) || variants[0] || null;
    } else {
      variant = variants.find(v => {
        if (Array.isArray(v.option_value_ids) && v.option_value_ids.length === selected.length) {
          const idsMatch = v.option_value_ids.every((value, i) => String(value) === selected[i]);
          if (idsMatch) return true;
        }
        if (Array.isArray(v.options) && v.options.length === selectedLabels.length) {
          return v.options.every((value, i) => String(value).trim().toLowerCase() === selectedLabels[i].trim().toLowerCase());
        }
        return false;
      });
    }
    groups.forEach(group => {
      const checked = group.querySelector('[data-option-value]:checked');
      const output = group.querySelector('[data-option-current]');
      if (output) output.textContent = checked?.dataset.optionLabel || '';
    });
    updateFulfillmentMetadata();
    syncDimensionCard();
    if (!variant) {
      if (add) add.disabled = true;
      if (addLabel) addLabel.textContent = labels.unavailable;
      return;
    }
    idInput.value = variant.id;
    if (price) price.textContent = formatMoney(variant.price, moneyFormat);
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, moneyFormat);
    if (stickyAdd) stickyAdd.textContent = variant.available ? labels.add : labels.soldout;
    if (stickyButton) stickyButton.disabled = !variant.available;
    if (compare) {
      const showCompare = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
      compare.classList.toggle('is-hidden', !showCompare);
      compare.textContent = showCompare ? formatMoney(variant.compare_at_price, moneyFormat) : '';
    }
    if (add) add.disabled = !variant.available;
    if (addLabel) addLabel.textContent = variant.available ? labels.add : labels.soldout;
    if (identifier) identifier.textContent = variant.sku || identifier.dataset.fallback || String(variant.id);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url.toString());
  }

  root.addEventListener('input', event => {
    if (event.target.matches('[data-country-code-input]')) {
      event.target.value = String(event.target.value || '').replace(/[^a-z]/gi, '').slice(0, 4).toUpperCase();
    }
  });

  root.addEventListener('change', event => {
    if (event.target.matches('[data-option-value]')) {
      syncCarStyleUI();
      update();
      ensureDistinctPersonalizationColors();
    }
    if (event.target.matches('[data-property-color-input]')) {
      const group = event.target.closest('[data-property-color-group]');
      const current = group?.querySelector('[data-property-color-current]');
      const canonical = group?.querySelector('[data-property-color-canonical]');
      const property = group?.querySelector('[data-property-color-property]');
      if (current) current.textContent = event.target.dataset.propertyLabel || event.target.value || '';
      if (canonical) canonical.value = event.target.dataset.propertyCanonical || event.target.value || '';
      if (property) property.value = event.target.dataset.propertyLabel || event.target.value || '';
      ensureDistinctPersonalizationColors();
    }
    if (event.target.matches('[data-file-input]')) {
      const drop = event.target.closest('.file-drop');
      const label = drop?.querySelector('[data-file-name]');
      const file = event.target.files?.[0];
      if (drop) drop.classList.toggle('has-file', Boolean(file));
      if (label) label.textContent = file ? `${labels.fileSelected}: ${file.name}` : labels.fileEmpty;
    }
  });

  const productForm = root.querySelector('form.product-form');
  productForm?.addEventListener('submit', event => {
    const validDistinct = ensureDistinctPersonalizationColors();
    if (!validDistinct) {
      event.preventDefault();
      const note = secondaryColorGroup?.querySelector('[data-color-constraint-note]');
      if (note) {
        note.textContent = labels.colorsDiffer;
        note.classList.add('is-error');
        note.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  });

  function initOrnamentConfigurator() {
    const configurator = root.querySelector('[data-ornament-configurator]');
    if (!configurator || configurator.dataset.initialized === 'true') return;
    configurator.dataset.initialized = 'true';

    const modeInputs = [...configurator.querySelectorAll('[data-ornament-mode]')];
    const presetFields = configurator.querySelector('[data-ornament-preset-properties]');
    const customFields = configurator.querySelector('[data-ornament-custom-fields]');
    if (!modeInputs.length || !presetFields || !customFields) return;

    function syncOrnamentMode() {
      const custom = configurator.querySelector('[data-ornament-mode]:checked')?.value === 'custom';
      presetFields.disabled = custom;
      customFields.disabled = !custom;
      customFields.hidden = !custom;
      configurator.querySelectorAll('[data-ornament-mode-card]').forEach(card => {
        const input = card.querySelector('[data-ornament-mode]');
        const active = Boolean(input?.checked);
        card.classList.toggle('is-selected', active);
        if (input) input.setAttribute('aria-expanded', active && input.value === 'custom' ? 'true' : 'false');
      });
      if (custom) syncAllOrnamentSlots();
    }

    function syncOrnamentSlot(slot) {
      const design = slot.querySelector('[data-ornament-design]');
      const image = slot.querySelector('[data-ornament-slot-image]');
      const previewName = slot.querySelector('[data-ornament-preview-name]');
      const color = slot.querySelector('[data-ornament-color]:checked');
      const currentColor = slot.querySelector('[data-ornament-current-color]');
      const colorCode = slot.querySelector('[data-ornament-color-code]');
      const selectedDesign = design?.selectedOptions?.[0];
      if (image && selectedDesign?.dataset.imageUrl) image.src = selectedDesign.dataset.imageUrl;
      if (previewName && design) previewName.textContent = design.value;
      if (currentColor && color) currentColor.textContent = color.dataset.colorLabel || color.value;
      if (colorCode && color) colorCode.value = color.dataset.colorCode || color.value;
    }

    function syncAllOrnamentSlots() {
      customFields.querySelectorAll('[data-ornament-slot]').forEach(syncOrnamentSlot);
    }

    modeInputs.forEach(input => input.addEventListener('change', syncOrnamentMode));
    customFields.addEventListener('change', event => {
      if (event.target.matches('[data-ornament-design], [data-ornament-color]')) {
        const slot = event.target.closest('[data-ornament-slot]');
        if (slot) syncOrnamentSlot(slot);
      }
    });
    syncOrnamentMode();
  }

  initOrnamentConfigurator();
  applyPreferredVariantDefaults();
  syncCarStyleUI();
  update();
  ensureDistinctPersonalizationColors({ repair: true });
}
document.querySelectorAll('[data-product-root]').forEach(root => { initProduct(root); initKbcDelivery(root); });



function toBase64Url(value) {
  const bytes = new TextEncoder().encode(String(value || ''));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function initKbcDelivery(root) {
  const card = root?.querySelector('[data-kbc-delivery]');
  const trigger = card?.querySelector('[data-kbc-delivery-trigger]');
  const form = root?.querySelector('form.product-form');
  const idInput = form?.querySelector('[data-variant-id]');
  const quantityInput = form?.querySelector('input[name="quantity"]');
  if (!card || !trigger || !form || !idInput) return;
  if (card.dataset.ready === 'true') return;
  card.dataset.ready = 'true';

  trigger.addEventListener('click', () => {
    if (!form.reportValidity()) return;

    const variantId = String(idInput.value || '').trim();
    const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
    if (!variantId) return;

    const properties = {};
    const entries = new FormData(form).entries();
    for (const [name, value] of entries) {
      const match = String(name).match(/^properties\[(.+)\]$/);
      if (!match) continue;
      if (typeof value !== 'string') continue;
      if (!value.trim()) continue;
      properties[match[1]] = value;
    }
    properties['KBC delivery'] = 'KBC Rijeka';

    const rootPath = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const params = new URLSearchParams();
    params.set('properties', toBase64Url(JSON.stringify(properties)));
    params.set('attributes[kbc_delivery]', 'KBC Rijeka');
    params.set('attributes[kbc_delivery_address]', 'Ul. Vjekoslava Dukića 7, 51000 Rijeka, Croatia');
    params.set('checkout[shipping_address][address1]', 'Ul. Vjekoslava Dukića 7');
    params.set('checkout[shipping_address][address2]', 'KBC Rijeka');
    params.set('checkout[shipping_address][city]', 'Rijeka');
    params.set('checkout[shipping_address][country]', 'Croatia');
    params.set('checkout[shipping_address][zip]', '51000');

    trigger.disabled = true;
    trigger.setAttribute('aria-busy', 'true');
    trigger.textContent = card.dataset.labelBusy || 'Opening checkout…';

    const checkoutUrl = `${rootPath}cart/${encodeURIComponent(variantId)}:${quantity}?${params.toString()}`;
    window.location.assign(checkoutUrl);
  });
}

function shippingRateMoney(value, currency) {
  const amount = Number.parseFloat(String(value ?? '0'));
  if (!Number.isFinite(amount)) return String(value ?? '');
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency || 'EUR'
    }).format(amount);
  } catch (_) {
    return `${amount.toFixed(2)} ${currency || 'EUR'}`;
  }
}

function initShippingEstimator(root) {
  if (!root || root.dataset.ready === 'true') return;
  root.dataset.ready = 'true';

  const country = root.querySelector('[data-shipping-country]');
  const postal = root.querySelector('[data-shipping-postal]');
  const button = root.querySelector('[data-shipping-calculate]');
  const results = root.querySelector('[data-shipping-results]');
  if (!country || !button || !results) return;

  const labels = {
    loading: root.dataset.labelLoading || 'Checking current shipping rates…',
    results: root.dataset.labelResults || 'Available shipping',
    free: root.dataset.labelFree || 'Free',
    noRates: root.dataset.labelNoRates || 'No shipping rate was returned for this destination and cart.',
    error: root.dataset.labelError || 'We couldn’t load shipping rates right now.',
    updateFirst: root.dataset.labelUpdateFirst || 'Update the cart after changing quantities before checking shipping.'
  };
  const currency = root.dataset.currency || 'EUR';
  const shopRoot = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

  function showMessage(message, state = '') {
    results.replaceChildren();
    const p = document.createElement('p');
    p.className = `cart-shipping-estimator__message${state ? ` is-${state}` : ''}`;
    p.textContent = message;
    results.appendChild(p);
  }

  function showRates(rates) {
    results.replaceChildren();
    if (!Array.isArray(rates) || rates.length === 0) {
      showMessage(labels.noRates, 'empty');
      return;
    }

    const heading = document.createElement('p');
    heading.className = 'cart-shipping-estimator__results-title';
    heading.textContent = labels.results;
    results.appendChild(heading);

    const list = document.createElement('div');
    list.className = 'cart-shipping-estimator__rate-list';
    rates.forEach(rate => {
      const row = document.createElement('div');
      row.className = 'cart-shipping-estimator__rate';

      const name = document.createElement('span');
      name.textContent = rate.presentment_name || rate.name || rate.code || labels.results;

      const price = document.createElement('strong');
      const numeric = Number.parseFloat(String(rate.price ?? '0'));
      price.textContent = numeric === 0 ? labels.free : shippingRateMoney(rate.price, rate.currency || currency);

      row.append(name, price);
      list.appendChild(row);
    });
    results.appendChild(list);
  }

  const sleep = ms => new Promise(resolve => window.setTimeout(resolve, ms));

  async function getRates() {
    const changedQuantity = [...document.querySelectorAll('.cart-quantity input[name="updates[]"]')].some(input => input.value !== input.defaultValue);
    if (changedQuantity) {
      showMessage(labels.updateFirst, 'empty');
      return;
    }

    // Always send Shopify the ISO country code from localization.available_countries.
    // Localized labels such as "Hrvatska" can be rejected by the shipping-rate API
    // even though the same destination is valid at checkout.
    const countryCode = String(country.value || '').trim().toUpperCase();
    const params = new URLSearchParams();
    params.set('shipping_address[country]', countryCode);
    if (postal?.value?.trim()) params.set('shipping_address[zip]', postal.value.trim());

    const query = params.toString();
    const prepareUrl = `${shopRoot}cart/prepare_shipping_rates.json?${query}`;
    const asyncUrl = `${shopRoot}cart/async_shipping_rates.json?${query}`;

    button.disabled = true;
    root.setAttribute('aria-busy', 'true');
    showMessage(labels.loading, 'loading');

    try {
      const prepared = await fetch(prepareUrl, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      });
      if (!prepared.ok) throw new Error(`Shipping prepare failed (${prepared.status})`);

      let payload = null;
      const delays = [250, 500, 750, 1000, 1250, 1500];
      for (const delay of delays) {
        await sleep(delay);
        const response = await fetch(asyncUrl, {
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store'
        });
        if (!response.ok) throw new Error(`Shipping lookup failed (${response.status})`);
        payload = await response.json();
        if (payload && Array.isArray(payload.shipping_rates)) break;
      }

      if (!payload || !Array.isArray(payload.shipping_rates)) throw new Error('Shipping rates timed out');
      showRates(payload.shipping_rates);
    } catch (error) {
      console.warn('[Marante] Shipping-rate estimator:', error);
      showMessage(labels.error, 'error');
    } finally {
      button.disabled = false;
      root.removeAttribute('aria-busy');
    }
  }

  button.addEventListener('click', getRates);
  postal?.addEventListener('keydown', event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      getRates();
    }
  });
}


function initShippingEstimate(root) {
  if (!root || root.dataset.estimateReady === 'true') return;
  root.dataset.estimateReady = 'true';

  const countrySelect = root.querySelector('[data-estimate-country]');
  const serviceWrap = root.querySelector('[data-estimate-service-wrap]');
  const serviceSelect = root.querySelector('[data-estimate-service]');
  const rateRow = root.querySelector('[data-estimate-rate]');
  const totalOutput = root.querySelector('[data-estimate-total]');
  const mobileTotalOutput = document.querySelector('[data-estimate-mobile-total]');
  const noteOutput = root.querySelector('[data-estimate-note]');
  const progress = root.querySelector('[data-estimate-progress]');
  const progressFill = root.querySelector('[data-estimate-progress-fill]');
  const progressCopy = root.querySelector('[data-estimate-progress-copy]');
  if (!countrySelect || !rateRow || !totalOutput) return;

  const labels = {
    shipping: root.dataset.labelShipping || 'Estimated shipping',
    estimatedTotal: root.dataset.labelEstimatedTotal || 'Estimated total',
    atCheckout: root.dataset.labelAtCheckout || 'Confirmed at checkout',
    free: root.dataset.labelFree || 'Free',
    from: root.dataset.labelFrom || 'From',
    standard: root.dataset.labelStandard || 'Standard',
    express: root.dataset.labelExpress || 'Express',
    days: root.dataset.labelDays || 'business days',
    threshold: root.dataset.labelThreshold || 'You are {{ amount }} away from free delivery.',
    unlocked: root.dataset.labelUnlocked || 'You have unlocked free standard delivery.',
    weightCheck: root.dataset.labelWeightCheck || 'This destination uses weight-based rates. The final price is confirmed at checkout.'
  };

  const countrySets = {
    croatia: new Set(['HR']),
    euOne: new Set(['AT','BE','CZ','DK','FR','DE','HU','IT','NL','PL','SK','SI']),
    euTwo: new Set(['BG','CY','EE','FI','GR','IE','LV','LT','LU','MT','PT','RO','ES','SE']),
    international: new Set(['BD','CA','CN','IN','JP'])
  };

  const subtotalPresentment = Math.max(0, Number(root.dataset.cartSubtotalCents || 0) / 100);
  const weightGrams = Math.max(0, Number(root.dataset.cartWeightGrams || 0));
  const baseCurrency = String(root.dataset.baseCurrency || 'EUR').toUpperCase();
  const shopCurrency = String(root.dataset.currency || 'EUR').toUpperCase();
  const rawCurrencyRate = window.Shopify?.currency?.rate;
  const hasCurrencyRate = rawCurrencyRate !== undefined && rawCurrencyRate !== null && Number.isFinite(Number(rawCurrencyRate)) && Number(rawCurrencyRate) > 0;
  const currencyRate = hasCurrencyRate ? Number(rawCurrencyRate) : 1;
  const canConvertFromEuro = baseCurrency === 'EUR' && (shopCurrency === 'EUR' || hasCurrencyRate);
  const euroSubtotal = canConvertFromEuro ? subtotalPresentment / currencyRate : (shopCurrency === 'EUR' ? subtotalPresentment : null);

  function formatMoneyFromEuro(amount) {
    if (!canConvertFromEuro && shopCurrency !== 'EUR') return `€${amount.toFixed(2)}`;
    const value = canConvertFromEuro ? amount * currencyRate : amount;
    const currency = canConvertFromEuro ? shopCurrency : 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(value);
    } catch (_) {
      return `${value.toFixed(2)} ${currency}`;
    }
  }

  function formatMoneyFromPresentment(amount) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: shopCurrency }).format(amount);
    } catch (_) {
      return `${amount.toFixed(2)} ${shopCurrency}`;
    }
  }

  function setRate(name, price, options = {}) {
    rateRow.replaceChildren();
    const label = document.createElement('span');
    label.textContent = name;
    const amount = document.createElement('strong');
    const hasKnownPrice = options.free || Number.isFinite(price);
    amount.textContent = options.free ? labels.free : (hasKnownPrice ? `${options.from ? `${labels.from} ` : ''}${formatMoneyFromEuro(price)}` : labels.atCheckout);
    rateRow.append(label, amount);
    const totalText = hasKnownPrice && (canConvertFromEuro || shopCurrency === 'EUR')
      ? `${options.from ? `${labels.from} ` : ''}${formatMoneyFromPresentment(subtotalPresentment + (options.free ? 0 : (canConvertFromEuro ? price * currencyRate : price)))}`
      : labels.atCheckout;
    totalOutput.textContent = totalText;
    if (mobileTotalOutput) mobileTotalOutput.textContent = totalText;
  }

  function hideProgress() {
    if (progress) progress.hidden = true;
  }

  function setEstimateNote(text) {
    if (noteOutput) noteOutput.textContent = text;
  }

  function showThreshold(threshold) {
    if (!progress || !progressCopy || !progressFill || euroSubtotal === null) {
      hideProgress();
      return;
    }
    progress.hidden = false;
    const ratio = Math.max(0, Math.min(100, (euroSubtotal / threshold) * 100));
    progressFill.style.width = `${ratio}%`;
    if (euroSubtotal >= threshold) {
      progressCopy.textContent = labels.unlocked;
    } else {
      const remaining = threshold - euroSubtotal;
      progressCopy.textContent = labels.threshold.replace('{{ amount }}', formatMoneyFromEuro(remaining));
    }
  }

  function renderEstimate() {
    const code = String(countrySelect.value || '').toUpperCase();
    const countryOption = countrySelect.selectedOptions[0];
    const intl = countrySets.international.has(code);
    if (serviceWrap) serviceWrap.hidden = !intl;
    if (serviceSelect && !intl) serviceSelect.value = 'standard';
    hideProgress();

    // Estimates are based on the shipping profiles shown by the merchant.
    // Unknown weight breaks and destinations deliberately fall back to checkout confirmation.
    setEstimateNote(root.dataset.labelEstimateNote || 'An estimate based on the current shipping setup. The final rate is confirmed at checkout.');
    if (countrySets.croatia.has(code)) {
      const free = euroSubtotal !== null && euroSubtotal >= 50;
      setRate(`${labels.standard} · 3–5 ${labels.days}`, 4, { free });
      showThreshold(50);
      return;
    }
    if (countrySets.euOne.has(code)) {
      if (weightGrams > 1000) {
        setRate(labels.atCheckout, Number.NaN);
        setEstimateNote(labels.weightCheck);
        hideProgress();
      } else {
        setRate(`${labels.standard} · 5–10 ${labels.days}`, 10.5);
      }
      return;
    }
    if (countrySets.euTwo.has(code)) {
      setRate(`${labels.standard} · 5–10 ${labels.days}`, 12.5, { from: true });
      setEstimateNote(labels.weightCheck);
      return;
    }
    if (intl) {
      const express = serviceSelect?.value === 'express';
      const threshold = express ? 95 : 90;
      const price = express ? 23 : 21;
      const free = euroSubtotal !== null && euroSubtotal >= threshold;
      const delivery = express ? '4–6' : '5–10';
      setRate(`${express ? labels.express : labels.standard} · ${delivery} ${labels.days}`, price, { free });
      showThreshold(threshold);
      return;
    }

    setRate(labels.atCheckout, Number.NaN);
    hideProgress();
  }

  function syncExactCountry(code) {
    const exactCountry = document.querySelector('[data-shipping-country]');
    if (exactCountry && exactCountry.value !== code) {
      exactCountry.value = code;
      exactCountry.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  countrySelect.addEventListener('change', () => {
    syncExactCountry(countrySelect.value);
    renderEstimate();
  });
  serviceSelect?.addEventListener('change', renderEstimate);

  const exactCountry = document.querySelector('[data-shipping-country]');
  exactCountry?.addEventListener('change', () => {
    if (countrySelect.value !== exactCountry.value) {
      countrySelect.value = exactCountry.value;
      renderEstimate();
    }
  });

  root.querySelector('[data-estimate-open-exact]')?.addEventListener('click', () => {
    const exactEstimator = document.querySelector('[data-shipping-estimator]');
    if (exactEstimator) {
      exactEstimator.open = true;
      exactEstimator.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });

  renderEstimate();
}

document.querySelectorAll('[data-shipping-estimate]').forEach(initShippingEstimate);

document.querySelectorAll('[data-shipping-estimator]').forEach(initShippingEstimator);


(function initMaranteMobileGallery(){
  function initGallery(gallery){
    if(!gallery || gallery.dataset.galleryBound === 'true') return;
    var dotsWrap=gallery.parentElement?.querySelector('[data-gallery-dots]');
    if(!dotsWrap) return;
    var slides=[...gallery.querySelectorAll('.product-media')];
    var dots=[...dotsWrap.querySelectorAll('[data-gallery-dot]')];
    if(slides.length<2 || dots.length!==slides.length) return;
    gallery.dataset.galleryBound='true';
    var raf=0;
    function sync(){
      cancelAnimationFrame(raf);
      raf=requestAnimationFrame(function(){
        var center=gallery.getBoundingClientRect().left+gallery.clientWidth/2;
        var best=0,dist=Infinity;
        slides.forEach(function(slide,i){
          var r=slide.getBoundingClientRect();
          var d=Math.abs((r.left+r.width/2)-center);
          if(d<dist){dist=d;best=i;}
        });
        dots.forEach(function(dot,i){
          var active=i===best;
          dot.classList.toggle('is-active',active);
          dot.setAttribute('aria-current',active?'true':'false');
        });
      });
    }
    gallery.addEventListener('scroll',sync,{passive:true});
    dots.forEach(function(dot){
      dot.addEventListener('click',function(){
        var i=Number(dot.dataset.galleryDot||0);
        slides[i]?.scrollIntoView({behavior:'smooth',block:'nearest',inline:'center'});
      });
    });
    sync();
  }
  function boot(){document.querySelectorAll('[data-product-gallery]').forEach(initGallery);}
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',boot); else boot();
  document.addEventListener('shopify:section:load',boot);
})();


/* Marante Studio V6: hero collection sampler. One product per source group, shuffled on each load. */
function initHeroRandomGalleries(scope = document) {
  const galleries = [];
  if (scope && scope.matches && scope.matches('[data-hero-random-gallery]')) galleries.push(scope);
  if (scope && scope.querySelectorAll) galleries.push(...scope.querySelectorAll('[data-hero-random-gallery]'));

  galleries.forEach(gallery => {
    if (gallery.dataset.galleryReady === 'true') return;
    const sourceNodes = [...gallery.querySelectorAll('[data-hero-product]')];
    const source = sourceNodes.map(node => ({
      group: String(node.dataset.group || ''),
      image: String(node.dataset.image || ''),
      url: String(node.dataset.url || ''),
      title: String(node.dataset.title || ''),
      price: String(node.dataset.price || '')
    })).filter(item => item.image && item.url);
    const slots = [...gallery.querySelectorAll('[data-hero-slot-content]')].sort((a,b) => Number(a.dataset.heroSlotContent) - Number(b.dataset.heroSlotContent));
    if (!source.length || !slots.length) return;

    const shuffle = list => {
      const copy = list.slice();
      for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy;
    };
    const picked = [];
    const used = new Set();
    ['personalized', 'car', 'desk'].forEach(group => {
      const candidates = shuffle(source.filter(item => item.group === group && !used.has(item.url)));
      if (candidates.length && picked.length < slots.length) {
        picked.push(candidates[0]);
        used.add(candidates[0].url);
      }
    });
    if (picked.length < Math.min(3, slots.length)) {
      shuffle(source.filter(item => !used.has(item.url))).forEach(item => {
        if (picked.length < Math.min(3, slots.length)) {
          picked.push(item);
          used.add(item.url);
        }
      });
    }
    const ordered = shuffle(picked);
    gallery.dataset.count = String(ordered.length);

    slots.forEach((slot, index) => {
      const item = ordered[index];
      if (!item) {
        slot.hidden = true;
        return;
      }
      slot.hidden = false;
      slot.href = item.url;
      slot.setAttribute('aria-label', item.title || 'View product');
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.title;
      image.width = 1200;
      image.height = 1200;
      image.loading = index === 0 ? 'eager' : 'lazy';
      image.decoding = 'async';
      image.fetchPriority = index === 0 ? 'high' : 'auto';

      const caption = document.createElement('span');
      caption.className = 'hero-product-caption';
      const title = document.createElement('span');
      title.className = 'hero-product-title';
      title.textContent = item.title;
      const price = document.createElement('span');
      price.className = 'hero-product-price';
      price.textContent = item.price;
      caption.append(title, price);
      slot.replaceChildren(image, caption);
    });
    gallery.dataset.galleryReady = 'true';
    gallery.classList.add('hero-product-gallery--ready');
  });
}

/* Appearance preference: system, light or dark. The explicit choice lives in a cookie. */
function initMaranteAppearance() {
  const root = document.documentElement;
  const validModes = ['system', 'light', 'dark'];
  const media = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  const meta = document.querySelector('[data-theme-color]');

  function apply(mode, persist = false) {
    const selected = validModes.includes(mode) ? mode : 'system';
    const effective = selected === 'system' ? (media && media.matches ? 'dark' : 'light') : selected;
    root.dataset.colorMode = selected;
    root.dataset.theme = effective;
    if (meta) meta.setAttribute('content', effective === 'dark' ? (meta.dataset.darkColor || '#10191b') : (meta.dataset.lightColor || '#fffdfb'));
    document.querySelectorAll('[data-theme-mode-switcher]').forEach(switcher => {
      const trigger = switcher.querySelector('[data-theme-mode-toggle]');
      const label = switcher.querySelector('[data-theme-mode-label]');
      const selectedOption = switcher.querySelector(`[data-theme-mode-choice="${selected}"]`);
      if (label && selectedOption) label.textContent = selectedOption.querySelector('span:nth-child(2)')?.textContent?.trim() || selected;
      if (trigger) {
        trigger.setAttribute('aria-label', `${switcher.dataset.appearanceLabel || 'Appearance'}: ${label?.textContent || selected}`);
        trigger.title = trigger.getAttribute('aria-label');
        switcher.querySelectorAll('[data-theme-trigger-icon]').forEach(icon => {
          icon.hidden = icon.dataset.themeTriggerIcon !== selected;
        });
      }
      switcher.querySelectorAll('[data-theme-mode-choice]').forEach(option => {
        const current = option.dataset.themeModeChoice === selected;
        option.setAttribute('aria-checked', current ? 'true' : 'false');
      });
    });
    if (persist && typeof setCookie === 'function') setCookie('marante_theme_mode', selected, 365);
  }

  const initial = root.dataset.colorMode || (typeof getCookie === 'function' ? getCookie('marante_theme_mode') : '') || 'system';
  apply(initial, false);

  if (!document.documentElement.dataset.appearanceEventsReady) {
    document.documentElement.dataset.appearanceEventsReady = 'true';
    document.addEventListener('click', event => {
      const trigger = event.target.closest('[data-theme-mode-toggle]');
      if (trigger) {
        const switcher = trigger.closest('[data-theme-mode-switcher]');
        const menu = switcher?.querySelector('[data-theme-mode-menu]');
        if (!switcher || !menu) return;
        const nextOpen = menu.hidden;
        document.querySelectorAll('[data-theme-mode-menu]').forEach(other => { other.hidden = true; });
        document.querySelectorAll('[data-theme-mode-toggle]').forEach(button => button.setAttribute('aria-expanded', 'false'));
        menu.hidden = !nextOpen;
        trigger.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
        return;
      }
      const choice = event.target.closest('[data-theme-mode-choice]');
      if (choice) {
        apply(choice.dataset.themeModeChoice, true);
        document.querySelectorAll('[data-theme-mode-menu]').forEach(menu => { menu.hidden = true; });
        document.querySelectorAll('[data-theme-mode-toggle]').forEach(button => button.setAttribute('aria-expanded', 'false'));
        return;
      }
      if (!event.target.closest('[data-theme-mode-switcher]')) {
        document.querySelectorAll('[data-theme-mode-menu]').forEach(menu => { menu.hidden = true; });
        document.querySelectorAll('[data-theme-mode-toggle]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      }
    });
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('[data-theme-mode-menu]').forEach(menu => { menu.hidden = true; });
      document.querySelectorAll('[data-theme-mode-toggle]').forEach(button => {
        if (button.getAttribute('aria-expanded') === 'true') button.focus();
        button.setAttribute('aria-expanded', 'false');
      });
    });
  }

  if (media && !root.dataset.appearanceMediaReady) {
    root.dataset.appearanceMediaReady = 'true';
    const syncSystem = () => {
      if ((root.dataset.colorMode || 'system') === 'system') apply('system', false);
    };
    if (media.addEventListener) media.addEventListener('change', syncSystem);
    else if (media.addListener) media.addListener(syncSystem);
  }
}

/* Small scroll entrances make the homepage feel more composed without hiding content if unsupported. */
function initMaranteMotion(scope = document) {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;
  const selectors = '.category-card--showcase, .catalog-discovery-card, .catalog-group-heading, .product-card, .step, .trust-item, .motion-grid, .custom-order-section .split, .faq details';
  const elements = [];
  if (scope && scope.matches && scope.matches(selectors)) elements.push(scope);
  if (scope && scope.querySelectorAll) elements.push(...scope.querySelectorAll(selectors));
  if (!elements.length) return;
  document.body.classList.add('has-motion-reveal');
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in-view');
      observer.unobserve(entry.target);
    });
  }, { root: null, rootMargin: '0px 0px -7% 0px', threshold: 0.08 });
  elements.forEach((element, index) => {
    if (element.dataset.motionRevealReady === 'true') return;
    element.dataset.motionRevealReady = 'true';
    element.classList.add('motion-reveal');
    element.style.setProperty('--reveal-delay', `${(index % 5) * 48}ms`);
    observer.observe(element);
  });
}

function bootMaranteV6(scope = document) {
  initHeroRandomGalleries(scope);
  initMaranteMotion(scope);
  initMaranteAppearance();
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bootMaranteV6());
} else {
  bootMaranteV6();
}
document.addEventListener('shopify:section:load', event => bootMaranteV6(event.target));


// V9 — Christmas countdown. Uses the visitor's local time and rolls over annually.
function initChristmasCountdowns() {
  const countdowns = [...document.querySelectorAll('[data-christmas-countdown]')];
  if (!countdowns.length) return;

  countdowns.forEach((node) => {
    if (node.dataset.countdownReady === 'true') return;
    node.dataset.countdownReady = 'true';

    const parts = {
      days: node.querySelector('[data-countdown-days]'),
      hours: node.querySelector('[data-countdown-hours]'),
      minutes: node.querySelector('[data-countdown-minutes]'),
      seconds: node.querySelector('[data-countdown-seconds]')
    };
    const complete = node.querySelector('[data-countdown-complete]');
    const units = node.querySelector('.christmas-countdown__units');
    const pad = (value) => String(value).padStart(2, '0');

    const update = () => {
      const now = new Date();
      const year = now.getFullYear();
      const christmasStart = new Date(year, 11, 25, 0, 0, 0, 0);
      const nextDay = new Date(year, 11, 26, 0, 0, 0, 0);
      const isChristmasDay = now >= christmasStart && now < nextDay;
      const target = now >= nextDay ? new Date(year + 1, 11, 25, 0, 0, 0, 0) : christmasStart;

      if (isChristmasDay) {
        node.classList.add('is-complete');
        if (complete) {
          complete.hidden = false;
          complete.textContent = node.dataset.completeLabel || 'Merry Christmas!';
        }
        if (units) units.hidden = true;
        return;
      }

      node.classList.remove('is-complete');
      if (complete) complete.hidden = true;
      if (units) units.hidden = false;

      const total = Math.max(0, target.getTime() - now.getTime());
      const days = Math.floor(total / 86400000);
      const hours = Math.floor((total % 86400000) / 3600000);
      const minutes = Math.floor((total % 3600000) / 60000);
      const seconds = Math.floor((total % 60000) / 1000);

      if (parts.days) parts.days.textContent = String(days);
      if (parts.hours) parts.hours.textContent = pad(hours);
      if (parts.minutes) parts.minutes.textContent = pad(minutes);
      if (parts.seconds) parts.seconds.textContent = pad(seconds);
    };

    update();
    window.setInterval(update, 1000);
  });
}

window.addEventListener('DOMContentLoaded', initChristmasCountdowns);

// Pause decorative video when the visitor requests reduced motion.
window.addEventListener('DOMContentLoaded', () => {
  if (!window.matchMedia || !window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  document.querySelectorAll('.christmas-feature__video').forEach((video) => {
    try { video.pause(); } catch (_) {}
  });
});
